/**
 * Challenges Page
 * Create and manage all 11 challenge types with reward configuration,
 * QR code setup, submission review, and competitive settings.
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Modal, ModalBody, ModalFooter, Badge, toast } from '../components/ui';
import {
  challengesApi,
  Challenge,
  ChallengeSubmission,
  CreateChallengeData,
  ChallengeType,
  ChallengeRewardType,
  handleApiError,
} from '../lib/api';

const CHALLENGE_TEMPLATES: Array<{
  icon: string;
  name: string;
  description: string;
  type: ChallengeType;
  example: string;
  suggestedTarget: number;
  suggestedReward: number;
  suggestedRewardType: ChallengeRewardType;
  needsMetadata?: boolean;
  metadataHint?: string;
}> = [
  {
    icon: '💰', name: 'Spend Challenge', description: 'Reward customers who spend a target amount',
    type: 'SPEND_AMOUNT', example: 'Spend $50 this week, earn 100 points',
    suggestedTarget: 50, suggestedReward: 100, suggestedRewardType: 'POINTS',
  },
  {
    icon: '🎯', name: 'Visit Challenge', description: 'Reward frequent visits',
    type: 'VISIT_COUNT', example: 'Visit 10 times this month',
    suggestedTarget: 10, suggestedReward: 150, suggestedRewardType: 'POINTS',
  },
  {
    icon: '🔥', name: 'Streak Challenge', description: 'Reward daily consistency with streak freezes',
    type: 'STREAK', example: 'Check in 7 days in a row',
    suggestedTarget: 7, suggestedReward: 250, suggestedRewardType: 'POINTS',
    needsMetadata: true, metadataHint: 'Configure streak freeze options',
  },
  {
    icon: '🪙', name: 'Token Challenge', description: 'Reward high earners',
    type: 'EARN_TOKENS', example: 'Earn 500 points this month',
    suggestedTarget: 500, suggestedReward: 100, suggestedRewardType: 'POINTS',
  },
  {
    icon: '📱', name: 'QR Scavenger Hunt', description: 'Hide QR codes for customers to find',
    type: 'QR_SCAVENGER_HUNT', example: 'Find all 5 hidden QR codes',
    suggestedTarget: 5, suggestedReward: 500, suggestedRewardType: 'FREE_ITEM',
    needsMetadata: true, metadataHint: 'Configure QR code locations below',
  },
  {
    icon: '📸', name: 'Photo Challenge', description: 'Get UGC photos from customers for social media',
    type: 'PHOTO_CHALLENGE', example: 'Share your best latte art photo',
    suggestedTarget: 1, suggestedReward: 50, suggestedRewardType: 'POINTS',
    needsMetadata: true, metadataHint: 'Set photo prompt and approval settings',
  },
  {
    icon: '📣', name: 'Social Share', description: 'Reward customers for sharing on social media',
    type: 'SOCIAL_SHARE', example: 'Share a post with #YourCafe',
    suggestedTarget: 1, suggestedReward: 75, suggestedRewardType: 'DISCOUNT',
    needsMetadata: true, metadataHint: 'Set hashtag and platform requirements',
  },
  {
    icon: '🏆', name: 'Referral Race', description: 'Competitive challenge to refer the most friends',
    type: 'REFERRAL_RACE', example: 'Refer 5 friends this month',
    suggestedTarget: 5, suggestedReward: 300, suggestedRewardType: 'GIFT_CARD',
  },
  {
    icon: '🤝', name: 'Community Goal', description: 'Everyone works toward a shared goal',
    type: 'COMMUNITY_GOAL', example: '5000 community visits = free pastry for all',
    suggestedTarget: 5000, suggestedReward: 50, suggestedRewardType: 'FREE_ITEM',
  },
  {
    icon: '⏰', name: 'Happy Hour', description: 'Reward purchases during specific hours',
    type: 'TIME_BASED', example: 'Visit before 9am three times this week',
    suggestedTarget: 3, suggestedReward: 100, suggestedRewardType: 'POINTS',
    needsMetadata: true, metadataHint: 'Set target hours',
  },
  {
    icon: '🍕', name: 'Menu Explorer', description: 'Try specific items on the menu',
    type: 'ITEM_SPECIFIC', example: 'Try all 4 seasonal drinks',
    suggestedTarget: 4, suggestedReward: 200, suggestedRewardType: 'MERCH',
    needsMetadata: true, metadataHint: 'List required items',
  },
];

const REWARD_TYPES: Array<{ value: ChallengeRewardType; label: string; icon: string }> = [
  { value: 'POINTS', label: 'Bonus Points', icon: '🪙' },
  { value: 'GIFT_CARD', label: 'Gift Card', icon: '💳' },
  { value: 'DISCOUNT', label: 'Discount', icon: '🏷️' },
  { value: 'MERCH', label: 'Merchandise', icon: '👕' },
  { value: 'FREE_ITEM', label: 'Free Item', icon: '🎁' },
  { value: 'CUSTOM', label: 'Custom Reward', icon: '✨' },
];

const TYPE_LABELS: Record<ChallengeType, string> = {
  SPEND_AMOUNT: 'Spend $',
  VISIT_COUNT: 'Visits',
  EARN_TOKENS: 'Points',
  STREAK: 'Days',
  TIME_BASED: 'Visits',
  ITEM_SPECIFIC: 'Items',
  QR_SCAVENGER_HUNT: 'QR Codes',
  PHOTO_CHALLENGE: 'Photos',
  SOCIAL_SHARE: 'Shares',
  REFERRAL_RACE: 'Referrals',
  COMMUNITY_GOAL: 'Total',
};

const TYPE_ICONS: Record<ChallengeType, string> = {
  SPEND_AMOUNT: '💰',
  VISIT_COUNT: '🎯',
  EARN_TOKENS: '🪙',
  STREAK: '🔥',
  TIME_BASED: '⏰',
  ITEM_SPECIFIC: '🍕',
  QR_SCAVENGER_HUNT: '📱',
  PHOTO_CHALLENGE: '📸',
  SOCIAL_SHARE: '📣',
  REFERRAL_RACE: '🏆',
  COMMUNITY_GOAL: '🤝',
};

interface QrCodeEntry {
  id: string;
  label: string;
  hint: string;
}

type TabFilter = 'all' | 'active' | 'completed' | 'submissions';

export const ChallengesPage: React.FC = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [submissions, setSubmissions] = useState<ChallengeSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<typeof CHALLENGE_TEMPLATES[0] | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    type: ChallengeType;
    targetValue: number;
    b3trReward: number;
    rewardType: ChallengeRewardType;
    rewardValue: number;
    rewardDescription: string;
    startDate: string;
    endDate: string;
    maxParticipants: number | undefined;
    isCompetitive: boolean;
    topNWinners: number;
    // Metadata fields
    qrCodes: QrCodeEntry[];
    photoPrompt: string;
    requireApproval: boolean;
    hashtag: string;
    socialPlatform: string;
    requireProof: boolean;
    allowFreeze: boolean;
    freezesPerChallenge: number;
    startHour: number;
    endHour: number;
  }>({
    name: '',
    description: '',
    type: 'SPEND_AMOUNT',
    targetValue: 0,
    b3trReward: 0,
    rewardType: 'POINTS',
    rewardValue: 0,
    rewardDescription: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    maxParticipants: undefined,
    isCompetitive: false,
    topNWinners: 10,
    qrCodes: [{ id: 'qr1', label: '', hint: '' }],
    photoPrompt: '',
    requireApproval: true,
    hashtag: '',
    socialPlatform: 'instagram',
    requireProof: true,
    allowFreeze: false,
    freezesPerChallenge: 1,
    startHour: 6,
    endHour: 9,
  });

  useEffect(() => {
    loadChallenges();
    loadSubmissions();
  }, []);

  const loadChallenges = async () => {
    setIsLoading(true);
    try {
      const response = await challengesApi.getAll();
      setChallenges(response.data.data);
    } catch (error) {
      console.error('Failed to load challenges:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      const response = await challengesApi.getPendingSubmissions();
      setSubmissions(response.data.data);
    } catch {
      // Submissions may not exist yet
    }
  };

  const handleTemplateSelect = (template: typeof CHALLENGE_TEMPLATES[0]) => {
    setSelectedTemplate(template);
    setFormData({
      ...formData,
      type: template.type,
      targetValue: template.suggestedTarget,
      b3trReward: template.suggestedReward,
      rewardType: template.suggestedRewardType,
      name: template.example,
      description: template.description,
      isCompetitive: template.type === 'REFERRAL_RACE',
    });
  };

  const buildMetadata = (): Record<string, any> | undefined => {
    switch (formData.type) {
      case 'QR_SCAVENGER_HUNT':
        return {
          qrCodes: formData.qrCodes.filter(qr => qr.label.trim()).map((qr, i) => ({
            id: `qr${i + 1}`,
            label: qr.label,
            hint: qr.hint,
          })),
        };
      case 'PHOTO_CHALLENGE':
        return {
          prompt: formData.photoPrompt,
          requireApproval: formData.requireApproval,
          hashtag: formData.hashtag || undefined,
        };
      case 'SOCIAL_SHARE':
        return {
          platform: formData.socialPlatform,
          hashtag: formData.hashtag,
          requireProof: formData.requireProof,
        };
      case 'STREAK':
        return {
          allowFreeze: formData.allowFreeze,
          freezesPerChallenge: formData.freezesPerChallenge,
        };
      case 'TIME_BASED':
        return {
          startHour: formData.startHour,
          endHour: formData.endHour,
        };
      default:
        return undefined;
    }
  };

  const handleCreateChallenge = async () => {
    setCreateError(null);

    if (!formData.name.trim()) { setCreateError('Challenge name is required.'); return; }
    if (!formData.description.trim()) { setCreateError('Description is required.'); return; }
    if (!formData.targetValue || formData.targetValue <= 0) { setCreateError('Target value must be greater than 0.'); return; }
    if (!formData.b3trReward || formData.b3trReward <= 0) { setCreateError('Point reward must be greater than 0.'); return; }
    if (!formData.endDate) { setCreateError('End date is required.'); return; }
    if (new Date(formData.endDate) <= new Date(formData.startDate)) { setCreateError('End date must be after start date.'); return; }

    if (formData.type === 'QR_SCAVENGER_HUNT') {
      const validQrs = formData.qrCodes.filter(qr => qr.label.trim());
      if (validQrs.length === 0) { setCreateError('Add at least one QR code location.'); return; }
      if (validQrs.length < formData.targetValue) { setCreateError(`Need at least ${formData.targetValue} QR code locations for the target.`); return; }
    }

    setIsCreating(true);
    try {
      const metadata = buildMetadata();
      const payload: CreateChallengeData = {
        name: formData.name,
        description: formData.description,
        type: formData.type,
        targetValue: formData.targetValue,
        b3trReward: formData.b3trReward,
        rewardType: formData.rewardType,
        rewardValue: formData.rewardType !== 'POINTS' ? formData.rewardValue : undefined,
        rewardDescription: formData.rewardDescription || undefined,
        startDate: formData.startDate,
        endDate: formData.endDate,
        maxParticipants: formData.maxParticipants,
        metadata,
        isCompetitive: formData.isCompetitive,
        topNWinners: formData.isCompetitive ? formData.topNWinners : undefined,
      };

      const response = await challengesApi.create(payload);
      setChallenges((prev) => [response.data.data, ...prev]);
      setShowCreateModal(false);
      resetForm();
      toast.success('Challenge created successfully!');
    } catch (error) {
      const apiError = handleApiError(error);
      setCreateError(apiError.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleReviewSubmission = async (submissionId: string, approved: boolean) => {
    try {
      await challengesApi.reviewSubmission(submissionId, approved);
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
      toast.success(approved ? 'Submission approved!' : 'Submission rejected.');
    } catch (error) {
      toast.error('Failed to review submission.');
    }
  };

  const handleDeleteChallenge = async (id: string) => {
    try {
      await challengesApi.delete(id);
      setChallenges(prev => prev.filter(c => c.id !== id));
      toast.success('Challenge deleted.');
    } catch {
      toast.error('Failed to delete challenge.');
    }
  };

  const resetForm = () => {
    setSelectedTemplate(null);
    setCreateError(null);
    setFormData({
      name: '', description: '', type: 'SPEND_AMOUNT', targetValue: 0, b3trReward: 0,
      rewardType: 'POINTS', rewardValue: 0, rewardDescription: '',
      startDate: new Date().toISOString().split('T')[0], endDate: '',
      maxParticipants: undefined, isCompetitive: false, topNWinners: 10,
      qrCodes: [{ id: 'qr1', label: '', hint: '' }],
      photoPrompt: '', requireApproval: true, hashtag: '', socialPlatform: 'instagram',
      requireProof: true, allowFreeze: false, freezesPerChallenge: 1, startHour: 6, endHour: 9,
    });
  };

  const addQrCode = () => {
    setFormData({
      ...formData,
      qrCodes: [...formData.qrCodes, { id: `qr${formData.qrCodes.length + 1}`, label: '', hint: '' }],
    });
  };

  const updateQrCode = (index: number, field: 'label' | 'hint', value: string) => {
    const updated = [...formData.qrCodes];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, qrCodes: updated });
  };

  const removeQrCode = (index: number) => {
    setFormData({ ...formData, qrCodes: formData.qrCodes.filter((_, i) => i !== index) });
  };

  const getStatusColor = (status: string): 'success' | 'default' | 'info' | 'error' => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'DRAFT': return 'default';
      case 'COMPLETED': return 'info';
      case 'EXPIRED': return 'error';
      default: return 'default';
    }
  };

  const filteredChallenges = challenges.filter(c => {
    if (activeTab === 'active') return c.status === 'ACTIVE';
    if (activeTab === 'completed') return c.status === 'COMPLETED' || c.status === 'EXPIRED';
    return true;
  });

  // --- Type-specific metadata form ---
  const renderMetadataForm = () => {
    switch (formData.type) {
      case 'QR_SCAVENGER_HUNT':
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">QR Code Locations</label>
              <button
                type="button"
                onClick={addQrCode}
                className="text-sm text-orange-500 hover:text-orange-600 font-medium"
              >
                + Add Location
              </button>
            </div>
            {formData.qrCodes.map((qr, i) => (
              <div key={i} className="flex gap-2 items-start">
                <div className="flex-1 space-y-1">
                  <Input
                    label={`Location ${i + 1}`}
                    value={qr.label}
                    onChange={(e) => updateQrCode(i, 'label', e.target.value)}
                    placeholder="e.g., By the counter"
                  />
                  <Input
                    value={qr.hint}
                    onChange={(e) => updateQrCode(i, 'hint', e.target.value)}
                    placeholder="Hint (optional): Look near the register"
                  />
                </div>
                {formData.qrCodes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeQrCode(i)}
                    className="mt-7 text-red-400 hover:text-red-600 p-1"
                  >
                    x
                  </button>
                )}
              </div>
            ))}
            <p className="text-xs text-gray-500">
              After creating, you'll get unique QR codes to print and place at each location.
            </p>
          </div>
        );

      case 'PHOTO_CHALLENGE':
        return (
          <div className="space-y-3">
            <Input
              label="Photo Prompt"
              value={formData.photoPrompt}
              onChange={(e) => setFormData({ ...formData, photoPrompt: e.target.value })}
              placeholder="e.g., Show us your best latte art!"
              helperText="Tell customers what kind of photo to take"
            />
            <Input
              label="Hashtag (optional)"
              value={formData.hashtag}
              onChange={(e) => setFormData({ ...formData, hashtag: e.target.value })}
              placeholder="e.g., #MyCafeArt"
            />
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.requireApproval}
                onChange={(e) => setFormData({ ...formData, requireApproval: e.target.checked })}
                className="w-4 h-4"
              />
              <label className="text-sm text-gray-700">
                Require manual approval (recommended - review photos before awarding points)
              </label>
            </div>
          </div>
        );

      case 'SOCIAL_SHARE':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Platform</label>
              <select
                value={formData.socialPlatform}
                onChange={(e) => setFormData({ ...formData, socialPlatform: e.target.value })}
                className="w-full border rounded-lg p-2.5 text-sm"
              >
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="facebook">Facebook</option>
                <option value="twitter">X (Twitter)</option>
                <option value="any">Any Platform</option>
              </select>
            </div>
            <Input
              label="Required Hashtag"
              value={formData.hashtag}
              onChange={(e) => setFormData({ ...formData, hashtag: e.target.value })}
              placeholder="e.g., #MyCafe"
              isRequired
            />
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.requireProof}
                onChange={(e) => setFormData({ ...formData, requireProof: e.target.checked })}
                className="w-4 h-4"
              />
              <label className="text-sm text-gray-700">
                Require screenshot/link proof (you'll review submissions)
              </label>
            </div>
          </div>
        );

      case 'STREAK':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.allowFreeze}
                onChange={(e) => setFormData({ ...formData, allowFreeze: e.target.checked })}
                className="w-4 h-4"
              />
              <label className="text-sm text-gray-700">
                Allow streak freezes (customers can miss one day without losing their streak)
              </label>
            </div>
            {formData.allowFreeze && (
              <Input
                type="number"
                label="Freezes Per Challenge"
                value={formData.freezesPerChallenge}
                onChange={(e) => setFormData({ ...formData, freezesPerChallenge: parseInt(e.target.value) || 1 })}
                helperText="How many times can a customer freeze their streak?"
              />
            )}
          </div>
        );

      case 'TIME_BASED':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Start Hour</label>
                <select
                  value={formData.startHour}
                  onChange={(e) => setFormData({ ...formData, startHour: parseInt(e.target.value) })}
                  className="w-full border rounded-lg p-2.5 text-sm"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">End Hour</label>
                <select
                  value={formData.endHour}
                  onChange={(e) => setFormData({ ...formData, endHour: parseInt(e.target.value) })}
                  className="w-full border rounded-lg p-2.5 text-sm"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Only visits during these hours count toward the challenge.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // --- Submissions review section ---
  const renderSubmissions = () => {
    if (submissions.length === 0) {
      return (
        <Card>
          <div className="text-center py-16 px-6">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-lg font-semibold mb-2 text-gray-900">No Pending Submissions</h3>
            <p className="text-sm text-gray-500">
              When customers submit photos or social share proofs, they'll appear here for your review.
            </p>
          </div>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {submissions.map((sub) => (
          <Card key={sub.id}>
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-gray-900">
                    {sub.customer?.firstName} {sub.customer?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{sub.challenge?.name}</p>
                </div>
                <Badge variant="warning">Pending</Badge>
              </div>

              {sub.type === 'PHOTO' && sub.data?.photoUrl && (
                <div className="mb-3">
                  <img
                    src={sub.data.photoUrl}
                    alt="Customer submission"
                    className="w-full h-48 object-cover rounded-lg"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  {sub.data.caption && (
                    <p className="text-sm text-gray-600 mt-2 italic">"{sub.data.caption}"</p>
                  )}
                </div>
              )}

              {sub.type === 'SOCIAL_PROOF' && (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm"><strong>Platform:</strong> {sub.data?.platform}</p>
                  {sub.data?.postUrl && (
                    <a href={sub.data.postUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500 hover:underline">
                      View Post
                    </a>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-400 mb-3">
                Submitted {new Date(sub.createdAt).toLocaleString()}
              </p>

              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleReviewSubmission(sub.id, true)}
                >
                  Approve
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleReviewSubmission(sub.id, false)}
                >
                  Reject
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Challenges & Campaigns</h1>
          <p className="text-base mt-2 text-gray-500">
            Create scavenger hunts, photo contests, streaks, referral races, and more
          </p>
        </div>
        <Button variant="primary" size="md" onClick={() => setShowCreateModal(true)} className="whitespace-nowrap self-start sm:self-auto">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Challenge
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Active', value: challenges.filter(c => c.status === 'ACTIVE').length, icon: '🎯', color: 'bg-green-50 text-green-600' },
          { label: 'Participants', value: challenges.reduce((s, c) => s + (c.participantCount ?? c.currentParticipants ?? 0), 0), icon: '👥', color: 'bg-blue-50 text-blue-600' },
          { label: 'Pending Review', value: submissions.length, icon: '📋', color: 'bg-yellow-50 text-yellow-600' },
          { label: 'Completed', value: challenges.filter(c => c.status === 'COMPLETED').length, icon: '✓', color: 'bg-purple-50 text-purple-600' },
          { label: 'Total Challenges', value: challenges.length, icon: '📊', color: 'bg-orange-50 text-orange-600' },
        ].map(stat => (
          <Card key={stat.label}>
            <div className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <span className="text-xl">{stat.icon}</span>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-400 tracking-wide">{stat.label}</p>
                  <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {([
          { key: 'all', label: 'All Challenges' },
          { key: 'active', label: 'Active' },
          { key: 'completed', label: 'Past' },
          { key: 'submissions', label: `Submissions${submissions.length ? ` (${submissions.length})` : ''}` },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'submissions' ? (
        renderSubmissions()
      ) : isLoading ? (
        <Card>
          <div className="text-center py-16 px-6">
            <p className="text-base text-gray-500">Loading challenges...</p>
          </div>
        </Card>
      ) : filteredChallenges.length === 0 ? (
        <Card>
          <div className="text-center py-16 px-6">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-lg font-semibold mb-2 text-gray-900">
              {activeTab === 'all' ? 'Create Your First Challenge' : 'No challenges in this category'}
            </h3>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              Challenges boost engagement through gamification. Try a QR scavenger hunt, photo contest, or streak challenge!
            </p>
            {activeTab === 'all' && (
              <Button variant="primary" size="md" onClick={() => setShowCreateModal(true)}>
                Create Your First Challenge
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredChallenges.map((challenge) => (
            <Card key={challenge.id}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{TYPE_ICONS[challenge.type] || '🎯'}</span>
                      <h3 className="text-lg font-semibold text-gray-900">{challenge.name}</h3>
                      <Badge variant={getStatusColor(challenge.status)}>{challenge.status}</Badge>
                      {challenge.isCompetitive && <Badge variant="warning">Ranked</Badge>}
                    </div>
                    <p className="text-sm text-gray-500">{challenge.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-400 mb-0.5">Target</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {TYPE_LABELS[challenge.type]} {challenge.targetValue}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-400 mb-0.5">Reward</p>
                    <p className="text-sm font-semibold text-orange-500">
                      {challenge.rewardDescription || `${challenge.b3trReward} points`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-400 mb-0.5">Participants</p>
                    <p className="text-sm text-gray-900">
                      {challenge.participantCount ?? challenge.currentParticipants}
                      {challenge.maxParticipants ? ` / ${challenge.maxParticipants}` : ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-400 mb-0.5">Ends</p>
                    <p className="text-sm text-gray-900">{new Date(challenge.endDate).toLocaleDateString()}</p>
                  </div>
                  {challenge.submissionCount != null && challenge.submissionCount > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-400 mb-0.5">Submissions</p>
                      <p className="text-sm text-gray-900">{challenge.submissionCount}</p>
                    </div>
                  )}
                  {challenge.communityProgress != null && (
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-400 mb-0.5">Community</p>
                      <p className="text-sm text-gray-900">
                        {Math.floor(challenge.communityProgress)} / {challenge.targetValue}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => handleDeleteChallenge(challenge.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Challenge Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); resetForm(); }}
        title="Create New Challenge"
        size="lg"
      >
        <ModalBody>
          {!selectedTemplate ? (
            <div>
              <p className="text-sm text-gray-500 mb-4">Choose a challenge type to get started</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {CHALLENGE_TEMPLATES.map((template) => (
                  <button
                    key={template.type}
                    onClick={() => handleTemplateSelect(template)}
                    className="p-4 bg-gray-50 hover:bg-orange-50 border-2 border-gray-200 hover:border-orange-400 rounded-lg text-left transition-all duration-200"
                  >
                    <div className="text-3xl mb-2">{template.icon}</div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">{template.name}</h4>
                    <p className="text-xs text-gray-500">{template.description}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Template banner */}
              <div className="p-3 bg-orange-50 rounded-lg border-l-4 border-orange-400 flex items-center gap-3">
                <span className="text-2xl">{selectedTemplate.icon}</span>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">{selectedTemplate.name}</h4>
                  <p className="text-xs text-gray-500">{selectedTemplate.description}</p>
                </div>
              </div>

              <Input
                label="Challenge Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Weekend Scavenger Hunt"
                isRequired
              />

              <Input
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What do customers need to do?"
                isRequired
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  label={`Target (${TYPE_LABELS[formData.type]})`}
                  value={formData.targetValue}
                  onChange={(e) => setFormData({ ...formData, targetValue: parseFloat(e.target.value) || 0 })}
                  isRequired
                />
                <Input
                  type="number"
                  label="Bonus Points"
                  value={formData.b3trReward}
                  onChange={(e) => setFormData({ ...formData, b3trReward: parseFloat(e.target.value) || 0 })}
                  isRequired
                  helperText="Points always awarded on completion"
                />
              </div>

              {/* Reward type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Reward Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {REWARD_TYPES.map(rt => (
                    <button
                      key={rt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, rewardType: rt.value })}
                      className={`p-2.5 rounded-lg border-2 text-left text-sm transition-all ${
                        formData.rewardType === rt.value
                          ? 'border-orange-400 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-lg">{rt.icon}</span>
                      <p className="text-xs font-medium mt-0.5">{rt.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {formData.rewardType !== 'POINTS' && (
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="number"
                    label={formData.rewardType === 'GIFT_CARD' ? 'Gift Card Value ($)' :
                           formData.rewardType === 'DISCOUNT' ? 'Discount Value' : 'Value ($)'}
                    value={formData.rewardValue}
                    onChange={(e) => setFormData({ ...formData, rewardValue: parseFloat(e.target.value) || 0 })}
                  />
                  <Input
                    label="Reward Description"
                    value={formData.rewardDescription}
                    onChange={(e) => setFormData({ ...formData, rewardDescription: e.target.value })}
                    placeholder="e.g., Free large latte"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="date"
                  label="Start Date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  isRequired
                />
                <Input
                  type="date"
                  label="End Date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  isRequired
                />
              </div>

              <Input
                type="number"
                label="Max Participants (Optional)"
                value={formData.maxParticipants || ''}
                onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value ? parseInt(e.target.value) : undefined })}
                helperText="Leave empty for unlimited"
              />

              {/* Competitive toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.isCompetitive}
                  onChange={(e) => setFormData({ ...formData, isCompetitive: e.target.checked })}
                  className="w-4 h-4"
                />
                <div>
                  <label className="text-sm font-medium text-gray-700">Competitive (Ranked)</label>
                  <p className="text-xs text-gray-500">Show leaderboard; top participants win</p>
                </div>
              </div>

              {formData.isCompetitive && (
                <Input
                  type="number"
                  label="Number of Winners"
                  value={formData.topNWinners}
                  onChange={(e) => setFormData({ ...formData, topNWinners: parseInt(e.target.value) || 10 })}
                  helperText="How many top-ranked participants receive the reward"
                />
              )}

              {/* Type-specific metadata */}
              {renderMetadataForm()}

              {/* Preview */}
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-900">
                  <strong>Preview:</strong> {formData.name || 'Your challenge'} | Target: {formData.targetValue} {TYPE_LABELS[formData.type]} | Reward: {formData.rewardDescription || `${formData.b3trReward} points`}
                  {formData.isCompetitive ? ' | Ranked' : ''} | {formData.startDate && formData.endDate
                    ? `${new Date(formData.startDate).toLocaleDateString()} - ${new Date(formData.endDate).toLocaleDateString()}`
                    : 'Set dates'}
                </p>
              </div>

              {createError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{createError}</p>
                </div>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="secondary"
            size="md"
            onClick={() => {
              if (selectedTemplate) { setSelectedTemplate(null); }
              else { setShowCreateModal(false); resetForm(); }
            }}
            className="flex-1"
          >
            {selectedTemplate ? 'Back' : 'Cancel'}
          </Button>
          {selectedTemplate && (
            <Button
              variant="primary"
              size="md"
              onClick={handleCreateChallenge}
              className="flex-1"
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create Challenge'}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ChallengesPage;
